"use client";
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { useRouter, useSearchParams, useParams } from "next/navigation";
import { useAuthentication } from "@/hooks/auth/useAuthentication";
import { useWaitingForApproval } from "@/hooks/auth/useWaitingForApproval";
import { Alert, Snackbar } from "@mui/material";
import { formatWithCountryCode, trimCountryCode } from "@/utils/phoneNumber";
import PageRoot from "@/components/PageRoot";
import Logo from "@/components/navigation/Logo";
import TimeoutDisplay from "@/components/auth/login/TimeoutDisplay";
import WaitingForApproval from "@/components/auth/login/WaitingForApproval";
import AuthForm from "@/components/auth/login/AuthForm";
import { StatsType } from "@/types/api";
import { sendAnalyticsEvent } from "@/services/analyticsService";
import LifeInfoAccordion from "./login/LifeInfoAccordion";

export default function AuthenticationPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const params = useParams();
  const invitationError = searchParams.get("invitationError");
  const confirmationError = searchParams.get("confirmationError");
  const genericError = searchParams.get("error");
  const analyticsEventSentRef = useRef<boolean>(false);
  const mobileNumberParameterValue =
    (Array.isArray(params?.mobileNumber)
      ? params.mobileNumber[0]
      : params?.mobileNumber) ?? "";
  const [openAlert, setOpenAlert] = useState(
    Boolean(invitationError ?? confirmationError ?? genericError)
  );

  const [phoneNumber, setPhoneNumber] = useState<string>();
  const {
    startPolling,
    isWaitingForApproval,
    success,
    statusMessage: approvalStatusMessage,
    hasTimedOut,
    MAX_POLLING_INTERVAL,
    startTime,
    handleTimeout,
    reset,
  } = useWaitingForApproval();
  const {
    handleAuthentication,
    statusMessage: authStatusMessage,
    isLoading,
    success: loginSuccess,
    requestId,
  } = useAuthentication();

  const handleSubmit = useCallback(
    async (phoneNumber: string) => {
      setPhoneNumber(phoneNumber);
      reset();
      await handleAuthentication(phoneNumber);
    },
    [handleAuthentication, reset]
  );

  useEffect(() => {
    if (loginSuccess && requestId && phoneNumber) {
      startPolling({
        msisdn: formatWithCountryCode(phoneNumber),
        requestId: requestId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginSuccess, requestId, phoneNumber]);

  useEffect(() => {
    if (success) {
      setTimeout(() => {
        window.location.href = "/";
      }, 100);
    }
  }, [success, router]);

  useEffect(() => {
    if (mobileNumberParameterValue && !analyticsEventSentRef.current) {
      const isStatsTypeExist = searchParams.get("type");
      const statsType =
        searchParams.get("type") === "invitation"
          ? StatsType.INVITATION_LINK_FOLLOWED
          : StatsType.CONFIRMATION_LINK_FOLLOWED;

      const analyticsKey = `analytics_sent_${mobileNumberParameterValue}_${statsType}`;
      const hasAlreadySentOnReload =
        sessionStorage.getItem(analyticsKey) === "true";

      if (!hasAlreadySentOnReload && isStatsTypeExist) {
        sendAnalyticsEvent({
          msisdn: mobileNumberParameterValue,
          statsType: statsType,
          accountId: undefined,
        });
        analyticsEventSentRef.current = true;
        sessionStorage.setItem(analyticsKey, "true");
      }
    }
  }, [mobileNumberParameterValue, searchParams]);

  const getErrorMessage = () => {
    if (invitationError) {
      return "The invitation link you used is invalid or has expired.";
    } else if (confirmationError) {
      return "The confirmation link you used is invalid or has expired.";
    } else if (genericError) {
      return "Something went wrong. Please try again later or contact support.";
    }
    return null;
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
  };

  function getAuthenticationComponent() {
    if (hasTimedOut) {
      return <TimeoutDisplay onRetry={() => window.location.reload()} />;
    } else if (isWaitingForApproval && !success) {
      return (
        <WaitingForApproval
          maxPollingInterval={MAX_POLLING_INTERVAL}
          isWaiting={isWaitingForApproval}
          onSuccess={success}
          hasTimedOut={hasTimedOut}
          startTime={startTime}
          onTimeout={handleTimeout}
        />
      );
    } else if (!isWaitingForApproval && success) {
      return (
        <div className="text-center">
          <p className="text-lg font-medium text-green-600">
            Authentication Successful!
          </p>
        </div>
      );
    } else {
      return (
        <>
          <AuthForm
            onSubmit={handleSubmit}
            isLoading={isLoading}
            authStatusMessage={authStatusMessage}
            approvalStatusMessage={approvalStatusMessage}
            mobileNumber={trimCountryCode(mobileNumberParameterValue)}
          />
          <div className="mt-4">
            <LifeInfoAccordion />
          </div>
        </>
      );
    }
  }


  return (
    <PageRoot>
      <Snackbar
        open={openAlert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {getErrorMessage()}
        </Alert>
      </Snackbar>

      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="bg-white p-6 shadow-lg w-full max-w-sm min-h-[450px] rounded-lg">
          <div className="flex text-center justify-center">
            <div style={{ height: "6.25rem" }}>
              <Logo dimension={120} />
            </div>
          </div>
          <h1 className="text-2xl text-center mb-4 font-bold">
            Verify Your Number
          </h1>
          {getAuthenticationComponent()}
        </div>
      </div>
    </PageRoot>
  );
}
